import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhThreatHuntingApi } from 'src/api/mindtime/wazuhThreatHuntingApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { guardEdrPreviewAction } from 'src/mindtime/utils/edrPreviewGuard';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhThreatHuntingEndpointItem,
  WazuhThreatHuntingFindingItem,
  WazuhThreatHuntingQuery,
  WazuhThreatHuntingSummaryData,
} from 'src/types/mindtime/wazuhThreatHunting';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';
import {
  emptyThreatHuntingFacets,
  mergeThreatHuntingFacets,
  type ThreatHuntingFacets,
} from './threatHuntingDetailModel';

export type { ThreatHuntingFacets };

export type ThreatHuntingFilters = {
  user?: string;
  mitreId?: string;
  eventType?: string;
  severityBand?: string;
  wazuhAgentId?: string;
  organizationId?: string;
};

export type ThreatHuntingSortParams = {
  column: string;
  sort: 'asc' | 'desc';
};

export type ThreatHuntingState = {
  loading: boolean;
  summary: WazuhThreatHuntingSummaryData | null;
  items: WazuhPagedData<WazuhThreatHuntingFindingItem> | null;
  endpoints: WazuhPagedData<WazuhThreatHuntingEndpointItem> | null;
  facets: ThreatHuntingFacets;
};

type BuildThreatHuntingQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  search?: string;
  filters?: ThreatHuntingFilters;
  page?: number;
  pageSize?: number;
  sortParams?: ThreatHuntingSortParams | null;
};

const emptyState = (): ThreatHuntingState => ({
  loading: true,
  summary: null,
  items: null,
  endpoints: null,
  facets: emptyThreatHuntingFacets(),
});

const buildThreatHuntingQuery = ({
  scope,
  profileOrgId,
  isReseller,
  search = '',
  filters = {},
  selectedEndpointId,
  page,
  pageSize,
  sortParams,
}: BuildThreatHuntingQueryInput & { selectedEndpointId?: string | null }): WazuhThreatHuntingQuery => {
  const { user, mitreId, eventType, severityBand, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(user ? { user } : {}),
    ...(mitreId ? { mitreId } : {}),
    ...(eventType ? { eventType } : {}),
    ...(severityBand ? { severityBand } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
    ...(sortParams ? { column: sortParams.column, sort: sortParams.sort } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useThreatHuntingExport = (search = '', filters: ThreatHuntingFilters = {}) => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { isPreviewMode } = useEdrDataGate('threat-hunting');
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    if (guardEdrPreviewAction(isPreviewMode, 'previewExportBlocked')) return;
    setExporting(true);
    mindtimeWazuhThreatHuntingApi
      .export(
        buildThreatHuntingQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          selectedEndpointId,
        })
      )
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'threat_hunting_export.xlsx';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useThreatHuntingExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, search, filters, selectedEndpointId, isPreviewMode]);

  return { exporting, handleExport };
};

export const useThreatHuntingData = (
  page = 0,
  pageSize = 10,
  search = '',
  filters: ThreatHuntingFilters = {},
  endpointsSortParams: ThreatHuntingSortParams | null = null
): ThreatHuntingState => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { skipLive, wait, isPreviewMode, getPreviewState } = useEdrDataGate('threat-hunting');
  const { canWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'threat-hunting',
  );
  const canSummary = canWidget('summary');
  const canResultsList = canWidget('resultsList');
  const canAffectedEndpoints = canWidget('affectedEndpoints');
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<ThreatHuntingState>(emptyState);

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        resellerId: scope.resellerId,
        startDate: scope.startDate,
        endDate: scope.endDate,
        isReseller,
        organizationId: filters.organizationId || '',
      }),
    [scope.resellerId, scope.startDate, scope.endDate, isReseller, filters.organizationId]
  );

  const { user, mitreId, eventType, severityBand, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId || '';

  useEffect(() => {
    setState((prev) => ({ ...prev, facets: emptyThreatHuntingFacets() }));
  }, [scopeKey]);

  useEffect(() => {
    if (wait) return undefined;
    if (isPreviewMode) {
      setState(getPreviewState() as ThreatHuntingState);
      return undefined;
    }

    if (!canSummary) {
      setState((prev) => ({ ...prev, summary: null }));
      return undefined;
    }

    let cancelled = false;

    const effectiveScope = buildEdrApiScope({
      scope,
      profileOrgId,
      isReseller,
      pageOrganizationId: organizationId || undefined,
    });

    mindtimeWazuhThreatHuntingApi
      .getSummary(effectiveScope)
      .then((summary) => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, summary }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useThreatHuntingData.summary');
          setState((prev) => ({
            ...prev,
            summary: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scope, profileOrgId, isReseller, organizationId, isPreviewMode, wait, getPreviewState, canSummary]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canResultsList) {
      setState((prev) => ({ ...prev, loading: false, items: null }));
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    mindtimeWazuhThreatHuntingApi
      .getFindings(
        buildThreatHuntingQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          page,
          pageSize,
          selectedEndpointId,
        })
      )
      .then((items) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          items,
          facets: mergeThreatHuntingFacets(items.items, prev.facets),
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useThreatHuntingData.findings');
          setState((prev) => ({
            ...prev,
            loading: false,
            items: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    page,
    pageSize,
    search,
    user,
    mitreId,
    eventType,
    severityBand,
    wazuhAgentId,
    organizationId,
    isPreviewMode,
    skipLive,
    canResultsList,
  ]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canAffectedEndpoints) {
      setState((prev) => ({ ...prev, endpoints: null }));
      return undefined;
    }

    let cancelled = false;
    const sortParams = endpointsSortParams ?? { column: 'threatMatches', sort: 'desc' as const };

    mindtimeWazuhThreatHuntingApi
      .listEndpoints(
        buildThreatHuntingQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          page: 0,
          pageSize,
          sortParams,
          selectedEndpointId,
        })
      )
      .then((endpoints) => {
        if (!cancelled) setState((prev) => ({ ...prev, endpoints }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useThreatHuntingData.endpoints');
          setState((prev) => ({ ...prev, endpoints: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    pageSize,
    search,
    user,
    mitreId,
    eventType,
    severityBand,
    wazuhAgentId,
    organizationId,
    endpointsSortParams?.column,
    endpointsSortParams?.sort,
    isPreviewMode,
    skipLive,
    canAffectedEndpoints,
  ]);

  return state;
};
